import { useEffect, useState, useRef } from 'react'
import { useApi } from '../lib/api'
import type { Client } from '../types'

// ---------------------------------------------------------------------------
// Inline form for create / edit
// ---------------------------------------------------------------------------
interface ClientFormProps {
  initial?: Partial<Client>
  onSave: (c: Client) => void
  onCancel: () => void
}

function ClientForm({ initial, onSave, onCancel }: ClientFormProps) {
  const { apiFetch } = useApi()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({
    name: initial?.name ?? '',
    email: initial?.email ?? '',
    phone: initial?.phone ?? '',
    company: initial?.company ?? '',
    notes: initial?.notes ?? '',
  })

  function set(field: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [field]: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const url = initial?.id ? `/api/clients/${initial.id}` : '/api/clients'
      const method = initial?.id ? 'PUT' : 'POST'
      const saved = await apiFetch<Client>(url, { method, body: JSON.stringify(form) })
      onSave(saved)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern des Kunden')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Name *</label>
        <input required value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Vollständiger Name oder Ansprechpartner" />
      </div>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Firma</label>
        <input value={form.company} onChange={(e) => set('company', e.target.value)} placeholder="Muster GmbH" />
      </div>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>E-Mail</label>
        <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="kontakt@beispiel.de" />
      </div>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Telefon</label>
        <input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+49 30 000000" />
      </div>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Notizen</label>
        <textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Interne Notizen…" />
      </div>
      {error && <div className="error-msg">{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Wird gespeichert…' : 'Speichern'}</button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function ClientsPage() {
  const { apiFetch } = useApi()
  const [clients, setClients] = useState<Client[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Client | null>(null)
  const [deleting, setDeleting] = useState<number | null>(null)
  const PAGE_SIZE = 25
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  function load(q: string, p: number) {
    setLoading(true)
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String((p - 1) * PAGE_SIZE),
    })
    if (q) params.set('q', q)
    apiFetch<{ clients: Client[]; total: number }>(`/api/clients?${params}`)
      .then(({ clients: c, total: t }) => { setClients(c); setTotal(t) })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load(search, page)
  }, [page]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearchChange(val: string) {
    setSearch(val)
    setPage(1)
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => load(val, 1), 300)
  }

  function handleSaved(c: Client) {
    if (editing) {
      setClients((prev) => prev.map((x) => (x.id === c.id ? c : x)))
    } else {
      load(search, page)
    }
    setShowForm(false)
    setEditing(null)
  }

  async function handleDelete(id: number) {
    if (!confirm('Diesen Kunden löschen? Verknüpfte Aufträge werden nicht gelöscht.')) return
    setDeleting(id)
    try {
      await apiFetch(`/api/clients/${id}`, { method: 'DELETE' })
      setClients((prev) => prev.filter((c) => c.id !== id))
      setTotal((t) => t - 1)
    } finally {
      setDeleting(null)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div>
      <h1 style={{ marginBottom: 20 }}>Kunden</h1>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Name, E-Mail, Firma, Telefon suchen…"
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          style={{ width: 320 }}
        />
        <button
          className="btn btn-primary"
          onClick={() => { setEditing(null); setShowForm(true) }}
        >
          + Neuer Kunde
        </button>
      </div>

      {/* Inline create form */}
      {showForm && !editing && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>Neuer Kunde</h3>
          <ClientForm onSave={handleSaved} onCancel={() => setShowForm(false)} />
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading">Kunden werden geladen…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Firma</th>
                <th>E-Mail</th>
                <th>Telefon</th>
                <th>Notizen</th>
                <th style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--color-muted)' }}>Keine Kunden gefunden</td></tr>
              )}
              {clients.map((c) => (
                <tr key={c.id}>
                  {editing?.id === c.id ? (
                    <td colSpan={6}>
                      <ClientForm
                        initial={editing}
                        onSave={handleSaved}
                        onCancel={() => setEditing(null)}
                      />
                    </td>
                  ) : (
                    <>
                      <td style={{ fontWeight: 500 }}>{c.name}</td>
                      <td>{c.company ?? <span style={{ color: 'var(--color-muted)' }}>—</span>}</td>
                      <td>{c.email ?? <span style={{ color: 'var(--color-muted)' }}>—</span>}</td>
                      <td>{c.phone ?? <span style={{ color: 'var(--color-muted)' }}>—</span>}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.notes ?? <span style={{ color: 'var(--color-muted)' }}>—</span>}
                      </td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => { setShowForm(false); setEditing(c) }}
                        >
                          Bearbeiten
                        </button>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12, color: 'var(--color-danger, #ef4444)' }}
                          disabled={deleting === c.id}
                          onClick={() => handleDelete(c.id)}
                        >
                          {deleting === c.id ? '…' : 'Löschen'}
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button className="btn btn-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>← Zurück</button>
          <span style={{ fontSize: 13, color: 'var(--color-muted)' }}>Seite {page} / {totalPages}</span>
          <button className="btn btn-secondary" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Weiter →</button>
        </div>
      )}
    </div>
  )
}
